import { inject, Service, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environment/environment';
import { Authentification } from './authentification';
import { extraireReponse } from './api';
import { ReponseApi } from './EntiteProjet';

export type CategorieSynthese = 'nouveaux' | 'retards' | 'messages' | 'bons';
export type TicketSynthese = {
  id: number | string;
  numero: string;
  libelle: string;
  installation: { id?: number; nom: string } | null;
};
export type SyntheseConnexionReponse = Record<
  CategorieSynthese,
  { total: number; tickets: TicketSynthese[] }
>;

/** Contrat de synthèse et compteur partagé : docs/synthese-connexion-backend.md. */
@Service()
export class SyntheseConnexionApi {
  private readonly http = inject(HttpClient);
  private readonly auth = inject(Authentification);
  private readonly url = `${environment.apiUrl}/utilisateur/synthese`;
  readonly totalNouveautes = signal<number | null>(null);
  private generation = 0;
  private enCours?: { session: number; promesse: Promise<SyntheseConnexionReponse> };

  reinitialiser(): void {
    // Une réponse de l'ancienne session ne doit pas rétablir son compteur.
    ++this.generation;
    this.enCours = undefined;
    this.totalNouveautes.set(null);
  }

  charger(): Promise<SyntheseConnexionReponse> {
    // La cloche et la modale partagent la requête en cours, sans cache après résolution.
    const session = this.auth.sessionVersion;
    if (this.enCours && this.enCours.session === session) return this.enCours.promesse;
    const promesse = this.chargerReponse().finally(() => {
      if (this.enCours?.promesse === promesse) this.enCours = undefined;
    });
    this.enCours = { session, promesse };
    return promesse;
  }

  private async chargerReponse(): Promise<SyntheseConnexionReponse> {
    const generation = ++this.generation;
    const session = this.auth.sessionVersion;
    try {
      const response = extraireReponse(
        await firstValueFrom(
          this.http.get<ReponseApi<SyntheseConnexionReponse>>(this.url, this.auth.prepareHeaders()),
        ),
      );
      if (!response) throw new Error('Synthèse indisponible');
      if (generation === this.generation && session === this.auth.sessionVersion)
        this.totalNouveautes.set(
          response.nouveaux.total +
            response.retards.total +
            response.messages.total +
            response.bons.total,
        );
      return response;
    } catch (erreur) {
      if (generation === this.generation && session === this.auth.sessionVersion)
        this.totalNouveautes.set(null);
      throw erreur;
    }
  }
}
